import { logger } from '@parrot-software/observability';

import type { ITableServicePort } from '@onboarding/application/ports';
import type { ServiceArea, TableServiceConfig } from '@onboarding/domain/value-objects';
import type { OnboardingHttpClientType } from '@onboarding/infrastructure/api';
import type { OnboardingApiMapperType } from '@onboarding/infrastructure/mappers';

const onbLog = logger.forDomain('ONB');
const DEFAULT_SEATS_PER_TABLE = 4;

/**
 * Concrete implementation of ITableServicePort.
 * Orchestrates HTTP client and mapper to fulfill application contracts.
 */
export const createTableServiceRepository = (props: {
  httpClient: OnboardingHttpClientType;
  mapper: OnboardingApiMapperType;
}): ITableServicePort => {
  const { httpClient, mapper } = props;

  const applyTableServiceSettings = async (config: TableServiceConfig): Promise<void> => {
    const apiRequest = mapper.mapTableServiceConfigToApiRequest(config);
    await httpClient.applyTableService(apiRequest);
  };

  const setupAreasWithTables = async (areas: ReadonlyArray<ServiceArea>): Promise<void> => {
    let totalTables = 0;

    for (const area of areas) {
      try {
        const createdArea = await httpClient.createServiceArea({ name: area.name });

        await httpClient.bulkCreateTables({
          serviceArea: createdArea.uuid,
          prefix: area.prefix,
          totalTables: area.tableCount,
          seatsPerTable: DEFAULT_SEATS_PER_TABLE,
        });

        totalTables += area.tableCount;
      } catch (error) {
        onbLog.error('table_area_create_failed', {
          'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
          'error.message': error instanceof Error ? error.message : String(error),
          'onb.area_name': area.name,
        });
        throw error;
      }
    }

    onbLog.debug('table_areas_bulk_created', {
      'onb.area_count': areas.length,
      'onb.total_tables': totalTables,
    });
  };

  const getExistingAreaNames = async (): Promise<string[]> => {
    const areas = await httpClient.getServiceAreas();
    return areas.map((area) => area.name);
  };

  return { applyTableServiceSettings, setupAreasWithTables, getExistingAreaNames };
};
